import { isEqual } from 'date-fns'

import {
  EMPTY_OBJECT,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../constants/common'
import { CLOSE_ON_ANCESTOR_SCROLL_ALWAYS } from '../../../constants/select'
import { useFloatingSelect } from '../../../hooks'
import { isEqualNullable } from '../../../utils/common'
import { createDay } from '../../../utils/date'
import { stopPropagation } from '../../../utils/events'
import { ButtonArrow } from '../../Button'
import { FloatingSelectDropdown } from '../../Floating'
import { DateInput } from '../DateInput'
import { DateSelect } from '../DateSelect'

import { DATE_PICKER_DROPDOWN_MIN_HEIGHT } from './DatePicker.constants'
import { shouldCloseOnSingleSelect } from './DatePicker.utils'

import * as styles from './DatePicker.module.css'

function DatePicker({
  Arrow = ButtonArrow,
  Input = DateInput,
  Select = DateSelect,
  arrowProps: {
    className: arrowClassName = styles.arrow,
    ...arrowProps
  } = EMPTY_OBJECT,
  children,
  createDate = createDay,
  disabled,
  dropdownPlacement = FLOATING_ELEMENT_PLACEMENT.bottomStart,
  dropdownProps,
  dropdownStyles: {
    minHeight: dropdownMinHeight = DATE_PICKER_DROPDOWN_MIN_HEIGHT,
    ...dropdownStyles
  } = EMPTY_OBJECT,
  maxValue,
  minValue,
  selectProps: {
    className: selectClassName = styles.select,
    ...selectProps
  } = EMPTY_OBJECT,
  shouldCloseOnSelect = shouldCloseOnSingleSelect,
  value,
  onChange,
  ...inputProps
}) {
  const { context, floatingStyles, getFloatingProps, isOpen, refs, setOpen } =
    useFloatingSelect({
      closeOnAncestorScroll: CLOSE_ON_ANCESTOR_SCROLL_ALWAYS,
      dropdownStyles: { ...dropdownStyles, minHeight: dropdownMinHeight },
      placement: dropdownPlacement,
    })

  function handleInputArrowClick(event) {
    stopPropagation(event)

    setOpen(currIsOpen => !currIsOpen)
  }

  function handleInputFocus() {
    setOpen(true)
  }

  function handleSelectChange(nextValue) {
    if (!isEqualNullable(nextValue, value, isEqual)) {
      onChange?.(nextValue)
    }

    if (shouldCloseOnSelect(nextValue)) {
      setOpen(false)
    }
  }

  return (
    <>
      <Input
        {...inputProps}
        afterButton={
          <Arrow
            {...arrowProps}
            className={arrowClassName}
            isOpen={isOpen}
            onClick={handleInputArrowClick}
          />
        }
        containerRef={refs.setReference}
        createDate={createDate}
        disabled={disabled}
        maxValue={maxValue}
        minValue={minValue}
        value={value}
        onChange={onChange}
        onFocus={handleInputFocus}
      />
      {children}
      <FloatingSelectDropdown
        {...dropdownProps}
        context={context}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        isOpen={isOpen}
        refs={refs}
      >
        <Select
          {...selectProps}
          className={selectClassName}
          createDate={createDate}
          disabled={disabled}
          maxValue={maxValue}
          minValue={minValue}
          value={value}
          onChange={handleSelectChange}
        />
      </FloatingSelectDropdown>
    </>
  )
}

export default DatePicker
